import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  PackagePlus,
  Upload,
  ListChecks,
  ClipboardList,
  Bot,
  Shield,
  ChevronLeft,
  ChevronDown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '../AuthProvider';
import NavItem from './NavItem';

const COLLAPSE_KEY = 'carbonroute_sidebar_collapsed';

export default function Sidebar({ onItemClick }) {
  const { user } = useAuth();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  });

  // Auto-expand sub-sections when user is on a child route.
  const [shipmentsOpen, setShipmentsOpen] = useState(
    () => location.pathname.startsWith('/shipments')
  );

  useEffect(() => {
    if (location.pathname.startsWith('/shipments')) setShipmentsOpen(true);
  }, [location.pathname]);

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0');
  };

  return (
    <aside
      className={cn(
        'app-chrome flex h-full flex-col border-r transition-[width] duration-200 ease-out',
        collapsed ? 'w-16' : 'w-60'
      )}
    >
      {/* Brand */}
      <div className={cn('flex h-14 md:h-16 items-center border-b border-border', collapsed ? 'justify-center' : 'gap-2.5 px-4')}>
        <img
          src="/assets/Arantree_logo_upscaled.png"
          alt="Arantree"
          className="h-8 w-auto flex-shrink-0 rounded-md dark:bg-white/90 dark:p-0.5"
        />
        {!collapsed && (
          <div className="min-w-0">
            <h1 className="text-[15px] font-semibold leading-tight tracking-tight text-foreground">
              CarbonRoute
            </h1>
            <p className="truncate text-[11px] text-muted-foreground">by Arantree</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className={cn('flex-1 overflow-y-auto py-3', collapsed ? 'px-2' : 'px-3')}>
        <NavItem icon={LayoutDashboard} label="Dashboard" to="/dashboard" collapsed={collapsed} onClick={onItemClick} />

        {/* Shipments group */}
        <SectionToggle
          icon={Package}
          label="Shipments"
          collapsed={collapsed}
          open={shipmentsOpen}
          onToggle={() => setShipmentsOpen((v) => !v)}
          isActive={location.pathname.startsWith('/shipments')}
        />
        {!collapsed && shipmentsOpen && (
          <div className="mt-1 space-y-1">
            <NavItem icon={PackagePlus} label="New Shipment" to="/shipments/new" nested collapsed={false} onClick={onItemClick} />
            <NavItem icon={Upload} label="Bulk Upload" to="/shipments/bulk" nested collapsed={false} onClick={onItemClick} />
          </div>
        )}

        <NavItem icon={ListChecks} label="My Activities" to="/scope3/activities" collapsed={collapsed} onClick={onItemClick} />
        <NavItem icon={ClipboardList} label="Commute Surveys" to="/scope3/surveys" collapsed={collapsed} onClick={onItemClick} />

        <div className="mx-2 my-3 border-t border-border" />

        <NavItem icon={Bot} label="Shanthi Chat" to="/shanthi" collapsed={collapsed} onClick={onItemClick} />

        {user?.is_admin && (
          <>
            <div className="mx-2 my-3 border-t border-border" />
            <NavItem icon={Shield} label="Admin" to="/admin" collapsed={collapsed} onClick={onItemClick} />
          </>
        )}
      </nav>

      {/* Collapse toggle */}
      <button
        onClick={toggleCollapse}
        className={cn(
          'hidden md:flex items-center justify-center border-t border-border py-3 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground',
          collapsed && 'rotate-180'
        )}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
    </aside>
  );
}

function SectionToggle({ icon: Icon, label, collapsed, open, onToggle, isActive }) {
  if (collapsed) {
    // When collapsed, show just the icon — clicking still toggles, but visual cue is the icon.
    return (
      <button
        onClick={onToggle}
        className={cn(
          'group relative flex w-full items-center justify-center px-2 py-2.5 rounded-lg text-sm font-medium transition-colors',
          isActive ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
        )}
        aria-label={label}
      >
        <Icon className="w-5 h-5" />
        <span className="pointer-events-none absolute left-full z-50 ml-2 whitespace-nowrap rounded-md border border-border bg-popover px-2 py-1 text-xs text-popover-foreground opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
          {label}
        </span>
      </button>
    );
  }
  return (
    <button
      onClick={onToggle}
      className={cn(
        'mt-0.5 flex w-full items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
        isActive ? 'text-foreground' : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
      )}
      aria-expanded={open}
    >
      <Icon className="w-4 h-4" aria-hidden="true" />
      <span className="flex-1 text-left">{label}</span>
      <ChevronDown className={cn('w-4 h-4 transition-transform', !open && '-rotate-90')} />
    </button>
  );
}
